import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import confetti from 'canvas-confetti';
import { FiCheckCircle, FiAlertTriangle, FiCopy, FiCheck } from 'react-icons/fi';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import BASE_URL from '../../config/config';
import { claves } from '../../queries/cliente';
import Loader from '../../components/Loader';

const COLORES_CONFETTI = ['#2c41a3', '#bbc5f1', '#2ecc71', '#ffffff', '#f1c40f'];

// Cortina de confetti cayendo desde el borde superior, repartida por todo el ancho.
const lanzarConfetti = () => {
    let tandas = 0;
    const intervaloLluvia = setInterval(() => {
        tandas += 1;
        confetti({
            particleCount: 3,
            startVelocity: 0,
            ticks: 300,
            gravity: 0.6,
            spread: 90,
            origin: { x: Math.random(), y: -0.1 },
            colors: COLORES_CONFETTI,
        });
        if (tandas >= 60) clearInterval(intervaloLluvia);
    }, 40);
};

const IdentificadorChip = ({ valor }) => {
    const { t } = useTranslation();
    const [copiado, setCopiado] = useState(false);

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(valor);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1800);
        } catch (error) {
            console.error('Error al copiar el identificador:', error);
        }
    };

    return (
        <button type="button" className="identificador" onClick={copiar} title={t('ticketCreado.copiarIdentificador')}>
            <span className="identificador-texto">
                <span className="identificador-label">{t('ticketCreado.identificador')}</span>
                <span className="identificador-valor">{valor}</span>
            </span>
            {copiado ? <FiCheck className="identificador-copiar copiado" /> : <FiCopy className="identificador-copiar" />}
        </button>
    );
};

const TicketCreado = () => {
    const { t } = useTranslation();
    const { identificador } = useParams(); // Obtener el ID del ticket de los parámetros de la URL
    const confettiLanzadoRef = useRef(false);

    const token = localStorage.getItem("AuthToken");

    // Comprobar si el token está presente antes de intentar decodificarlo
    let tokenInfo = {};
    if (token) {
      tokenInfo = jwtDecode(token);
    }

    // Solo se muestra si el ticket es de quien mira o si es personal de soporte
    const consultaTicket = useQuery({
        queryKey: claves.ticketPublico(identificador),
        queryFn: async () => {
            // El permiso lo resuelve el servidor: si no es tuyo, responde 403
            const respuesta = await fetch(`${BASE_URL}/ticket/${identificador}`, {
                headers: { Authorization: localStorage.getItem('AuthToken') || '' },
            });
            if (!respuesta.ok) return null;
            const datos = await respuesta.json();
            return datos.ticket || null;
        },
        enabled: Boolean(identificador),
    });

    const puedeVerlo = consultaTicket.data
        && (consultaTicket.data.usuario_creador === tokenInfo.usuario
            || tokenInfo.role === 'admin'
            || tokenInfo.role === 'tecnico');

    const ticket = puedeVerlo ? consultaTicket.data : null;
    const cargando = consultaTicket.isPending;

    // Lluvia de confetti al confirmar que el ticket se creó correctamente (una sola vez)
    useEffect(() => {
        if (ticket && !confettiLanzadoRef.current) {
            confettiLanzadoRef.current = true;
            lanzarConfetti();
        }
    }, [ticket]);

    if (cargando) {
        return <div className="loader-fullscreen"><Loader /></div>;
    }

    return (
        <main className='ticket-creado site-width'>
            <div className="verificar-card tc-enter">

                {ticket ? (() => {
                    const esGestionTecnico = ticket.abierto_por && ticket.abierto_por !== ticket.usuario_creador;
                    const esTecnicoOAdmin = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';

                    if (esGestionTecnico) {
                        // Técnico/admin creó el ticket en nombre de otro usuario
                        return (
                            <>
                                <div className="verificar-icon verificar-icon-exito tc-icon-pop"><FiCheckCircle /></div>
                                <h2>{t('ticketCreado.creadoTitulo')}</h2>
                                <p><Trans i18nKey="ticketCreado.abiertoParaTexto" values={{ usuario: ticket.usuario_creador }}>Has abierto un ticket en nombre de <strong>@usuario</strong>. El usuario podrá ver y gestionar su ticket desde su panel.</Trans></p>
                                <IdentificadorChip valor={ticket.identificador} />
                                <p className='recordatorio'>{t('ticketCreado.recordatorio')}</p>
                                <Link to={`/ticket/${ticket.identificador}`} className='btn-ver-ticket'>{t('ticketCreado.verTicket')}</Link>
                            </>
                        );
                    } else if (esTecnicoOAdmin) {
                        // Técnico/admin creó un ticket propio
                        return (
                            <>
                                <div className="verificar-icon verificar-icon-exito tc-icon-pop"><FiCheckCircle /></div>
                                <h2>{t('ticketCreado.creadoTitulo')}</h2>
                                <p>{t('ticketCreado.registradoTexto')}</p>
                                <IdentificadorChip valor={ticket.identificador} />
                                <Link to={`/ticket/${ticket.identificador}`} className='btn-ver-ticket'>{t('ticketCreado.verTicketCorto')}</Link>
                            </>
                        );
                    } else {
                        // Usuario normal
                        return (
                            <>
                                <div className="verificar-icon verificar-icon-exito tc-icon-pop"><FiCheckCircle /></div>
                                <h2>{t('ticketCreado.graciasTitulo')}</h2>
                                <p className="tc-texto-principal">{t('ticketCreado.graciasPrincipal')}</p>
                                <p className="tc-texto-secundario">{t('ticketCreado.graciasSecundario')}</p>
                                <IdentificadorChip valor={ticket.identificador} />
                                <Link to={`/ticket/${ticket.identificador}`} className='btn-ver-ticket'>{t('ticketCreado.verMiTicket')}</Link>
                            </>
                        );
                    }
                })() : (
                    <>
                        <div className="verificar-icon verificar-icon-error tc-icon-pop"><FiAlertTriangle /></div>
                        <h2>{t('ticketCreado.sinPermisosTitulo')}</h2>
                        <p><Trans i18nKey="ticketCreado.sinPermisosTexto"><a href='tel:+34 856 901 276'>contáctanos</a></Trans></p>
                    </>
                )}
            </div>
        </main>
    );
};

export default TicketCreado;
